import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FileDialog = ({ isOpen, onClose, file }) => {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!file) return null;

  const getFileSrc = () => {
    const extension = file.src.split('.').pop().toLowerCase();
    
    // Extensions that require Microsoft Office Online Viewer
    const officeTypes = ['pptx', 'ppt', 'docx', 'doc', 'xlsx', 'xls'];
    
    if (officeTypes.includes(extension)) {
      // Office Online requires an absolute URL. 
      // Note: This works on deployed sites. On localhost, it may show an error.
      const absoluteUrl = window.location.origin + file.src;
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absoluteUrl)}`;
    }
    
    // Standard PDF view with protection parameters
    // #toolbar=0 hides the download/print bar in most browsers
    return `${file.src}#toolbar=0&navpanes=0&scrollbar=1`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-6 lg:p-10">
          {/* Backdrop Blur Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md cursor-zoom-out"
          />

          {/* The Modal Window */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            className="relative w-full max-w-6xl h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
            // Blocks right-click on the entire modal wrapper
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Header / Toolbar */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-white z-[110] shadow-sm">
              <div className="flex flex-col">
                <h2 className="text-sm sm:text-lg font-bold text-gray-800 leading-none">
                  {file.type}
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="px-5 py-2 bg-gray-900 text-white hover:bg-black rounded-full transition-all text-xs font-bold shadow-md active:scale-95"
              >
                CLOSE
              </button>
            </div>

            {/* Viewer Content Area */}
            <div className="flex-grow bg-gray-100 relative overflow-hidden">
              
              {/* THE ACTUAL IFRAME */}
              <iframe
                src={getFileSrc()} 
                title="Document Viewer"
                className="w-full h-full border-none"
                allowFullScreen
              />

              {/* SECURITY SHIELDS */}
              
              {/* 1. TOP SHIELD: Blocks the area where Download/Print buttons usually appear.
                  This stays 'pointer-events-auto' so the buttons are unclickable. */}
              <div 
                className="absolute top-0 left-0 w-full h-[65px] z-[105] bg-transparent" 
                style={{ pointerEvents: 'auto' }}
              />

              {/* 2. BODY SHIELD: Blocks Right-Click across the whole file.
                  We use 'pointer-events-none' so that mouse scrolling works perfectly. */}
              <div 
                className="absolute inset-0 z-[101] bg-transparent pointer-events-none" 
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default FileDialog;